import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import type { BayesianProbe, ProbeOption } from '../types/engagement';
type Tone = 'light' | 'dark';
type Phase = 'prior' | 'evidence' | 'posterior';
interface BayesianOverlayProps {
  probe: BayesianProbe;
  tone?: Tone;
  onClose: () => void;
  /** Storyboard only — renders a specific step of the probe. */
  previewPhase?: Phase;
}
const EASE = [0.23, 1, 0.32, 1] as const;
const THEME = {
  light: {
    surface: 'bg-paper',
    divider: 'border-line',
    eyebrow: 'text-ozBlue',
    heading: 'text-ozNavy',
    body: 'text-mut',
    optionIdle: 'border-line bg-paper hover:border-ozBlue',
    optionMine: 'border-ozGreen bg-ozGreen/[0.12]',
    optionOther: 'border-line bg-paper opacity-45',
    optionText: 'text-ozNavy',
    share: 'text-ozBlue',
    priorEcho: 'bg-ozMist text-mut',
    priorEchoStrong: 'text-ozNavy',
    stat: 'text-ozNavy',
    primary: 'bg-ozNavy text-paper',
    secondary: 'border border-line text-ozNavy hover:border-ozBlue',
    track: 'bg-line',
    close: 'text-mut hover:text-ozNavy',
    fine: 'text-mut'
  },
  dark: {
    surface: 'bg-ozNavy',
    divider: 'border-paper/12',
    eyebrow: 'text-ozGreen',
    heading: 'text-paper',
    body: 'text-paper/70',
    optionIdle: 'border-paper/20 bg-paper/[0.05] hover:border-ozGreen',
    optionMine: 'border-ozGreen bg-ozGreen/[0.18]',
    optionOther: 'border-paper/10 bg-transparent opacity-40',
    optionText: 'text-paper',
    share: 'text-ozGreen',
    priorEcho: 'bg-paper/[0.07] text-paper/60',
    priorEchoStrong: 'text-paper',
    stat: 'text-ozGreen',
    primary: 'bg-ozGreen text-ozNavy',
    secondary: 'border border-paper/25 text-paper hover:border-ozGreen',
    track: 'bg-paper/20',
    close: 'text-paper/60 hover:text-paper',
    fine: 'text-paper/45'
  }
} as const;
function OptionList({
  options,
  selected,
  onSelect,
  theme
}: {
  options: ProbeOption[];
  selected: string | null;
  onSelect: (id: string) => void;
  theme: (typeof THEME)[Tone];
}) {
  return <div className="mt-4 flex flex-col gap-2" data-id="element-1" data-mp="1jbam38x9mw5">
      {options.map(option => {
      const isMine = selected === option.id;
      return <button key={option.id} type="button" onClick={() => !selected && onSelect(option.id)} aria-pressed={isMine} className={`flex items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-ozGreen ${selected ? isMine ? `cursor-default ${theme.optionMine}` : `cursor-default ${theme.optionOther}` : theme.optionIdle}`} data-id="element-2" data-mp="3tlato97k3d8i">
            <span className={`text-[12.5px] font-medium leading-snug ${theme.optionText}`} data-id="element-3" data-mp="30xncl45hv4qm">
              {option.label}
            </span>
            {selected && option.share !== undefined && <span className={`shrink-0 text-[12px] font-bold tabular-nums ${theme.share}`} data-id="element-4" data-mp="1bgjedep2dfmn">
                {option.share}%
              </span>}
          </button>;
    })}
    </div>;
}
export function BayesianOverlay({
  probe,
  tone = 'light',
  onClose,
  previewPhase
}: BayesianOverlayProps) {
  const [phase, setPhase] = useState<Phase>(previewPhase ?? 'prior');
  const [prior, setPrior] = useState<string | null>(previewPhase && previewPhase !== 'prior' ? probe.priorOptions[1].id : null);
  const [posterior, setPosterior] = useState<string | null>(previewPhase === 'posterior' ? probe.posteriorOptions[1].id : null);
  const theme = THEME[tone];
  const priorChoice = probe.priorOptions.find(o => o.id === prior);
  const posteriorChoice = probe.posteriorOptions.find(o => o.id === posterior);
  const choosePrior = (id: string) => {
    setPrior(id);
    window.setTimeout(() => setPhase('evidence'), 420);
  };
  const steps: Phase[] = ['prior', 'evidence', 'posterior'];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2,
    ease: EASE
  }} role="dialog" aria-modal="true" aria-label={probe.trigger} className={`absolute inset-0 z-20 flex flex-col ${theme.surface}`} data-id="element-5" data-mp="3m3d92rac99az">
      <div className={`flex items-center justify-between gap-3 border-b px-5 py-3 ${theme.divider}`} data-id="element-6" data-mp="dna1smn51mn6">
        <div className="flex items-center gap-1.5" aria-hidden="true" data-id="element-7" data-mp="8op8762e617p">
          {steps.map(s => <span key={s} className={`h-1 w-6 rounded-full transition-colors duration-150 ${steps.indexOf(phase) >= steps.indexOf(s) ? 'bg-ozGreen' : theme.track}`} data-id="element-8" data-mp="nm9c09njxqca" />)}
        </div>
        <button type="button" onClick={onClose} aria-label="Close question" className={`flex h-7 w-7 items-center justify-center rounded-full transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-ozGreen ${theme.close}`} data-id="element-9" data-mp="2pf70jtaapwrf">
          <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-10" data-mp="14wv0rv0o6mqh" />
        </button>
      </div>

      <div className="flex flex-1 flex-col overflow-y-auto px-5 py-5" data-id="element-11" data-mp="q41k469nw0k8">
        <AnimatePresence mode="wait" data-id="element-12" data-mp="v3xoi6m9tu0b">
          {phase === 'prior' && <motion.div key="prior" initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.22,
          ease: EASE
        }} className="flex flex-1 flex-col justify-center" data-id="element-13" data-mp="h57s6t79zyd0">
              <p className={`text-[9.5px] uppercase tracking-micro ${theme.eyebrow}`} data-id="element-14" data-mp="c7fo70tcwlsg">
                {probe.priorLabel}
              </p>
              <h3 className={`mt-3 font-display text-[19px] font-semibold leading-snug ${theme.heading}`} data-id="element-15" data-mp="21j81haed590g">
                {probe.priorQuestion}
              </h3>
              <OptionList options={probe.priorOptions} selected={prior} onSelect={choosePrior} theme={theme} data-id="element-16" data-mp="2515jotockef7" />
            </motion.div>}

          {phase === 'evidence' && <motion.div key="evidence" initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.22,
          ease: EASE
        }} className="flex flex-1 flex-col" data-id="element-17" data-mp="1q7117angeukh">
              <p className={`text-[9.5px] uppercase tracking-micro ${theme.eyebrow}`} data-id="element-18" data-mp="2e2vd251shnfm">
                {probe.evidenceLabel}
              </p>

              {priorChoice && <p className={`mt-2.5 rounded-lg px-3 py-2 text-[11px] leading-snug ${theme.priorEcho}`} data-id="element-19" data-mp="36kr9ktllw4zx">
                  Your prior:{' '}
                  <span className={theme.priorEchoStrong} data-id="element-20" data-mp="3a256cm6qe3vw">{priorChoice.label}</span>
                </p>}

              <p className={`mt-5 font-display text-[30px] font-bold leading-none tracking-tight ${theme.stat}`} data-id="element-21" data-mp="3e377x9o4es11">
                {probe.evidenceStat}
              </p>
              <p className={`mt-3.5 text-[12.5px] leading-relaxed ${theme.body}`} data-id="element-22" data-mp="3o2fxzrfv7mi4">
                {probe.evidenceText}
              </p>

              <button type="button" onClick={() => setPhase('posterior')} className={`mt-auto flex h-11 w-full items-center justify-center rounded-full text-[13px] font-semibold transition-opacity duration-150 hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ozGreen ${theme.primary}`} data-id="element-23" data-mp="3t0hqf2z6ky69">
                Update your view
              </button>
            </motion.div>}

          {phase === 'posterior' && <motion.div key="posterior" initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.22,
          ease: EASE
        }} className="flex flex-1 flex-col" data-id="element-24" data-mp="klftt1p33vzn">
              <p className={`text-[9.5px] uppercase tracking-micro ${theme.eyebrow}`} data-id="element-25" data-mp="1ew1f7n04nya0">
                {probe.posteriorLabel}
              </p>
              <h3 className={`mt-3 font-display text-[18px] font-semibold leading-snug ${theme.heading}`} data-id="element-26" data-mp="m7nhz9gv55zi">
                {probe.posteriorQuestion}
              </h3>

              <OptionList options={probe.posteriorOptions} selected={posterior} onSelect={setPosterior} theme={theme} data-id="element-27" data-mp="vyi66jdo6fnl" />

              {posteriorChoice && <motion.p initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.24,
            ease: EASE
          }} className={`mt-4 border-l-[3px] border-ozGreen pl-3.5 text-[12.5px] leading-relaxed ${theme.heading}`} data-id="element-28" data-mp="3g32n95qqu41">
                  {probe.closing}
                </motion.p>}

              <p className={`mt-auto pt-4 text-[8.5px] leading-snug ${theme.fine}`} data-id="element-29" data-mp="216t319luhjgb">
                {probe.reference}
              </p>

              <button type="button" onClick={onClose} className={`mt-3 flex h-11 w-full items-center justify-center rounded-full text-[13px] font-medium transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-ozGreen ${theme.secondary}`} data-id="element-30" data-mp="2ttxcbqrz0jxw">
                Back to the science
              </button>
            </motion.div>}
        </AnimatePresence>
      </div>
    </motion.div>;
}